/** @jsxImportSource theme-ui @jsxRuntime classic */
import React from 'react';

import { PageTransitionProvider } from './PageTransition';
import { ModalInner } from './ModalInner';

import { Box } from '.';

import type { FocusScopeProps } from '@hzdg/focus-scope';

export interface ModalProps extends FocusScopeProps<'div'> {
  id: string;
  /** Whether the modal is open. */
  isOpen?: boolean;
  /**
   * A callback that will be called when the Modal detects
   * an Escape key press or a click outside of the Modal content.
   *
   * Note that the Modal won't actually close itself! This callback
   * is the way to implement 'closing' the modal (i.e., by rerendering
   * with `isOpen` set to `false`).
   */
  onClose?: () => void;
  /**
   * A callback that will be called when the Modal and content has
   * finished its closing transition.
   */
  onCloseFinished?: () => void;
}

/**
 * `Modal` covers the viewport with an overlay
 * and displays modal content on top of the overlay
 * with accessible affordances baked in, such as:
 *   - traps focus within the modal content
 *   - automatically focuses on the modal content
 *   - restores focus to the previously focused element on close
 *   - closes when the ESC key is pressed
 *   - closes when a click is detected outside of the modal content
 *
 * It is also controllable via the `isOpen` and `onClose` props.
 */
export const Modal = (props: ModalProps): React.JSX.Element => (
  <PageTransitionProvider id={props.id}>
    <Box sx={{ position: 'relative', zIndex: 10000000000 }}>
      <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}>
        <ModalInner {...props} />
      </Box>
    </Box>
  </PageTransitionProvider>
);
